<template>
    <div class="video-player-dash">
        <video ref="videoElement" class="dash-js" />
    </div>
</template>

<script setup lang="ts">
    import type { PlayItemRestrictionsProcessor } from "@/components/Player/types/PlayItemRestrictionsProcessor";
    import { VideoPlayerEvent, type IVideoPlayer } from "@/components/Player/types"
    import shaka from "shaka-player/dist/shaka-player.compiled.js";
    import { usePlayerStore } from "@/stores/Player";
    import { eventsPlayer  } from './types';
    import { isSamsung } from "@ca.vmedia.tv/domains";
    import { TizenOSVersion } from '@/plugins/platform/platform-tizen';
    import { AppConstants } from "@/common";
    import { useVideoPlayerErrorHandler } from '@/composables/player/useVideoPlayerErrorHandler';

    const errorCategoriesToRetry = [
        // document: https://shaka-player-demo.appspot.com/docs/api/shaka.util.Error.html#.Category
        shaka.util.Error.Category.MANIFEST,
        shaka.util.Error.Category.NETWORK,
    ]; 

    type TAudioTrack = {
        id: string;
        language: string;
    };

    const PlayerStoreModule = usePlayerStore();
    const {
        saveSubtitles,
        saveAudio,
        setPlayerInitialized,
    } = PlayerStoreModule;

    const props = defineProps({
        customEventName: {
            type: String,
            default: "stateChanged"
        },
        subtitle: {
            type: String,
            default: ""
        },
        audio: {
            type: String,
            default: ""
        },
        volume: {
            type: Number,
            default: 100
        },
        itemProcessor: {
            type: [Object, null] as PropType<PlayItemRestrictionsProcessor | null>,
            required: true
        },
        isPlayBeginning: {
            type: Boolean,
            default: false
        },
        preferredAudio: {
            type: String,
            default: ''
        },
        isAvailableDescriptiveAudio: {
            type: Boolean,
            required: true
        },
        isSeeking: {
            type: Boolean,
            required: true
        },
        mediaSource: {
            type: [String, null] as PropType<string | null>,
            required: true,
        },
        token: {
            type: [String, null] as PropType<string | null>,
            required: true,
        },
    });

    const emit = defineEmits([
        ...eventsPlayer,
        'seeking',
        'seeked',
        'timeupdate',
        'hard-restart', 
        'error', 
        'ready', 
        'set-subtitles', 
        'set-audio', 
        'retry', 
        'set-preferred-audio', 
        'stateChanged',
        'error-507'
    ]);

    const { $platform } = useNuxtApp();

    let player: shaka.Player | null = null;
    const currentSubtitle: Ref<string> = ref('');
    const currentAudio: Ref<string> = ref('');
    const source: Ref<string | null> = ref(null);
    const videoElement: Ref<HTMLMediaElement | null> = ref(null);
    const durationPreviousProgram: Ref<number> = ref(0);

    let eventManager: shaka.util.EventManager | null = null;
    const instance = getCurrentInstance();
    const textTracks: Ref<shaka.extern.TextTrack[]> = ref([]);
    let isDisposing = false;

    const isOffline = computed((): boolean => !useOnline().value);

    let liveDifferenceTime: number = 0;
    const setOffsetTimeForLive = () =>{
        if(props.itemProcessor?.isLive && videoElement.value){
            liveDifferenceTime = Math.floor((TimeUtils.dateNow() / 1000) - videoElement.value.currentTime);
        }
    }
    const restart = () => {
        initialize().catch((e) => {
            console.error('Error during player restart:', e);
        });
    };
    const onManifestParsed = () => {
        if (player) {
            player.removeEventListener('manifestparsed', onManifestParsed);
            if (props.isPlayBeginning) {
                player.updateStartTime(0);
            }
        }
    };
    const onLoadedData = () => {
        initSubtitles();
        initAudio();
        emit('ready');
        setOffsetTimeForLive();
    };

    const mediaTime = ref(0);
    const lastMediaTime = ref(0);
    const _onTimeupdate = (): void => {
        if (props.isSeeking || isDisposing) {
            return;
        }

        mediaTime.value = getCurrentTime();
        if (lastMediaTime.value != mediaTime.value) {
            emit('timeupdate', mediaTime.value);
            lastMediaTime.value = mediaTime.value;
        }
    };

    const _onSeeking = (): void => {
        emit('seeking');
    };
    const _onkSeeked = (): void => {
        emit('seeked');
    };

    const initialize = async (): Promise<void> => {
        // Updated initialization based on docs: https://shaka-player-demo.appspot.com/docs/api/tutorial-basic-usage.html

        // Install built-in polyfills to patch browser incompatibilities.
        shaka.polyfill.installAll();
        if (!shaka.Player.isBrowserSupported()) {
            console.warn('Browser not supported!');
        }
        player = new shaka.Player();
        await player.attach(videoElement.value!);

        // Check if component was disposed during async operation
        if (isDisposing) {
            return;
        }
        
        eventManager = new shaka.util.EventManager();

        const _emitPlayerEvent = (eventName: string, eventObject: Event) => {
            if (eventName) {
                emit(eventName, new VideoPlayerEvent(eventName, instance as unknown as IVideoPlayer, player, eventObject));
            }
            if (eventObject) {
                emit(props.customEventName, new VideoPlayerEvent(eventName, instance as unknown as IVideoPlayer, player, eventObject));
            }
        };
        
        player.addEventListener('manifestparsed', onManifestParsed);
        eventManager?.listenOnce(videoElement.value, `loadeddata`, onLoadedData);
        
        eventsPlayer.forEach((event) => {
            eventManager?.listen(videoElement.value, event, (evt: Event) => {
               _emitPlayerEvent(event, evt);
            });
        });

        player.addEventListener('error', errorHandler);
        player.configure('streaming.failureCallback', errorHandler);
        setPlayerInitialized(true);
        eventManager?.listen(videoElement.value, 'timeupdate', _onTimeupdate);
        eventManager?.listen(videoElement.value, 'seeking', _onSeeking);
        eventManager?.listen(videoElement.value, 'seeked', _onkSeeked);
        
        await start();

        // decreasing the buffer length for Tizen 6.0 to avoid playback issues
       if (isSamsung && $platform.getOSVersion() === TizenOSVersion.V6_0) {
            player.configure({
                streaming: {
                    bufferingGoal: 15, // 15 seconds, default is 10 seconds
                    rebufferingGoal: 10, // 10 seconds, default is 0 seconds, always should be less than bufferingGoal
                    bufferBehind: 15, // 15 seconds, default is 30 seconds
                }
            });
        }
    };

    const switchOptionLoop = (url: string | null): void => {
        if (videoElement.value) {
            videoElement.value.loop = url?.includes(AppConstants.searchStringDemo) ?? false;
        }
    };
 
    const start = async (): Promise<void> => {
        if (isDisposing || !player) {
            return;
        }

        if (videoElement.value?.parentElement) {
            // Set the videoContainer to construct UITextDisplayer.
            player.setVideoContainer(videoElement.value.parentElement);
        }

        
        const widevineLaUrl = "https://widevine-license.vudrm.tech/proxy?token=" + encodeURIComponent(props.token!);

        switchOptionLoop(props.mediaSource);

        player.configure({
            textDisplayFactory: (p: shaka.Player) => new shaka.text.UITextDisplayer(p),
            drm: {
                servers: {
                    "com.widevine.alpha": widevineLaUrl
                }
            }
        });

        try {
            if (isDisposing || !player) {
                return;
            }
            
            await player.load(props.mediaSource);
            
            if (isDisposing || !player) {
                return;
            }
            
            setVolume(props.volume);
            await resume();
            
            if (!isDisposing) {
                source.value = props.mediaSource;
            }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            if (!isDisposing) {
                errorHandler(e);
            }
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const errorHandler = (e: any): void => {
        if (isDisposing || isOffline.value) {
            return;
        }
        const category = e.category ?? e.detail?.category;
        const severity = e.severity ?? e.detail?.severity;
        const code = e.code ?? e.detail?.code;
        const data = e.data ?? e.detail?.data;

        const isErrorResponseCode507 = data && Array.isArray(data) && data[1] === 507;

        if (code == shaka.util.Error.Code.BAD_HTTP_STATUS) {
            if (isErrorResponseCode507) {
                dispose(() => {
                    emit("error-507");
                }).catch((error) => {
                    console.error('Error during player disposal:', error);
                });
                return;
            }
            console.error('Error HTTP:', e);
        }
        if (errorCategoriesToRetry.includes(category)) {
            dispose(() => {
                emit("retry")
            }).catch((error) => {
                console.error('Error during player disposal:', error);
            });
        } else if (severity === shaka.util.Error.Severity.CRITICAL) {
            // custom handling of critical error
            dispose(() => {
                emit("error", new VideoPlayerEvent("error", instance as unknown as IVideoPlayer, player, e));
            }).catch((error) => {
                console.error('Error during player disposal:', error);
            });
        } else {
            // custom handling of recoverable error
            console.error('Error code:', code, 'object:', e);
        }
    };
 
    const getCurrentTime = (): number  =>  {
        if (!videoElement.value || !player) {
            return 0;
        }

        let progress = props.itemProcessor?.lengthInS || 0;
    
        if (props.itemProcessor?.isLive) {
            progress = (props.itemProcessor?.liveIsPausedOnce ? (videoElement.value.currentTime + liveDifferenceTime) : TimeUtils.dateNow() / 1000) - props.itemProcessor?.startTimeSec;
        } else if (props.itemProcessor?.isRestart){
            progress = videoElement.value.currentTime > props.itemProcessor?.startTimeSec ? videoElement.value.currentTime - props.itemProcessor?.startTimeSec : 0;
        } else if(props.itemProcessor?.isVCloud || props.itemProcessor?.isVOD) {
            progress = videoElement.value.currentTime - durationPreviousProgram.value;
        }
        
        return Math.floor(progress);
    };

    const resume = (): Promise<void> => {
        const element = videoElement.value;
        if (!element) {
            throw new Error('Video element is not available');
        }

        const playPromise = element.play();

        return playPromise.catch((error: unknown) => {
            const errorName = error instanceof DOMException
                ? error.name
                : (typeof error === 'object' && error !== null && 'name' in error
                    ? String((error as { name?: unknown }).name)
                    : undefined);

            if (errorName === 'AbortError') {
                // Expected when playback is paused before it actually starts
                return;
            }

            throw error;
        });
    };

    const setCurrentTime = (timeSec: number) => {
        if (videoElement.value && !!player) {
            let targetTime = timeSec + durationPreviousProgram.value;
            if (props.itemProcessor?.isLive || props.itemProcessor?.isRestart) {
                targetTime = props.itemProcessor?.startTimeSec + timeSec;
            }
            videoElement.value.currentTime = targetTime;
        }
    };

    const setVolume = (volume: number): void => {
        if (videoElement.value) {
            videoElement.value.volume = volume / 100;
        }
    };
 
    const isPaused = (): boolean => !!videoElement.value?.paused;

    const pause = (): void => {
        if (videoElement.value) {
            videoElement.value?.pause();
        }
    };
    const dispose = async (callback?: () => void): Promise<void> => {
        if (!isDisposing && !!player) {
            isDisposing = true;
            setPlayerInitialized(false);
            if (!isPaused()) {
                pause();
            }
            
            try {
                await player.destroy();
            } catch (error) {
                console.error('Error during player destroy:', error);
            }
            if (eventManager) {
                eventManager.release();
                eventManager = null;
            }
            player.removeEventListener('error', errorHandler);
            player = null;
            isDisposing = false;
            void nextTick(() => {
                callback?.();
            });
        }
    };

    const initSubtitles = (): void => {
        if (!player) {
            return;
        }
        textTracks.value = player.getTextTracks();
        emit('set-subtitles', textTracks.value);
        setSubtitlesState();
    };

    const setSubtitlesState = () => {
        if (!player) {
            return;
        };

        currentSubtitle.value = props.subtitle;

        if (props.subtitle && props.subtitle !== 'off') {
            let matchFound = false;
            
            // Try to find and select the preferred subtitle
            for (let i = 0; i < textTracks.value.length; i++) {
                if (String(textTracks.value[i].id) === props.subtitle) {
                    player.selectTextTrack(textTracks.value[i]);
                    currentSubtitle.value = String(textTracks.value[i].id);
                    matchFound = true;
                    break;
                }
            }
            
            // Fallback: if preferred subtitle not found, select first track
            if (!matchFound && textTracks.value.length > 0) {
                player.selectTextTrack(textTracks.value[0]);
                currentSubtitle.value = String(textTracks.value[0].id);
            }
        } else {
            player.selectTextTrack(null);
            currentSubtitle.value = 'off';
        }
        saveSubtitles(String(currentSubtitle.value));
    };

    const initAudio = () => {
        if (!player) {
            return;
        };
        const tracks = player.getAudioTracks();
        
        const audioTracks: TAudioTrack[] = tracks.map((track) =>  ({
            id: track.language,
            ...track
        }));
        emit('set-audio', audioTracks);
        for (let i = 0; i < audioTracks.length; i++) {
            if ((audioTracks[i].id === props.preferredAudio) && props.isAvailableDescriptiveAudio) {
                player.selectAudioTrack(tracks[i]);
                currentAudio.value = audioTracks[i].id;
            } else {
                const mainTrack = audioTracks[0];
                currentAudio.value = mainTrack.id;
            }
        }
        saveAudio(currentAudio.value);
    };
    const setAudio = () => {
        if (!player) {
            return;
        };
        const tracks = player.getAudioTracks();
        
        const audioTracks: TAudioTrack[] = tracks.map((track) =>  ({
            id: track.language,
            ...track
        }));
        const isPresentPreferredAudioTrack = audioTracks.find((track: TAudioTrack) => track.id === props.preferredAudio);
        for (let i = 0; i < audioTracks.length; i++) {
            if (audioTracks[i].id === props.audio) {
                player.selectAudioTrack(tracks[i]);
                currentAudio.value = audioTracks[i].id;
            } else {
                const mainTrack = audioTracks[0];
                currentAudio.value = mainTrack.id;
            }
        }
        saveAudio(currentAudio.value);
        if (props.isAvailableDescriptiveAudio && (isPresentPreferredAudioTrack || !props.preferredAudio)) {
            emit("set-preferred-audio", currentAudio.value);
        }
    }
    const reInitTracks = () => {
        initSubtitles();
        initAudio();
    };
    watch(
        () => props.itemProcessor,
        (itemProcessorNew: PlayItemRestrictionsProcessor | null, itemProcessorOld: PlayItemRestrictionsProcessor | null) => {
            if(itemProcessorNew && itemProcessorOld && (itemProcessorNew!.playItemId === itemProcessorOld!.playItemId)) {
                return;
            }
            durationPreviousProgram.value = durationPreviousProgram.value + (itemProcessorOld?.lengthInS ?? 0);
            reInitTracks();
        }
    );
    watch(
        () => props.volume,
        (value: number) => {
            setVolume(value);
        }
    );
    watch(
        () => props.subtitle,
        () => {
            setSubtitlesState();
        }
    );
    watch(
        () => props.audio,
        () => {
            setAudio();
        }
    );
    watch(
        isOffline,
        (val: boolean) => {
            if (val) {
                dispose().catch((e) => {
                    console.error('Error during player disposal:', e);
                });
            } else {
                emit("hard-restart");
            }
        }
    );

    useVideoPlayerErrorHandler(dispose, () => emit('retry'));
  
    onMounted(() => {
        if (!player && !!props.mediaSource) {
            initialize().catch((e) => {
                console.error('Error during player initialization:', e);
            });
        }
    });

    onBeforeUnmount(() => {
        dispose().catch((e) => {
            console.error('Error during player disposal:', e);
        });
    });

    defineExpose<IVideoPlayer>({
        getCurrentTime,
        setCurrentTime,
        pause,
        resume,
        restart,
        dispose,
        isPaused
    });

</script>

<style lang="scss">
    @import url("styles.scss");
</style>